/**
 * StaffServiceRequests — Inbox eingehender Kunden-Service-Anfragen
 * (admin-api /admin/service-requests, staff-gated).
 *
 * TQ #9696 (Staff-Migration Teil C, Bundle #9523). Portiert aus
 * customer-portal/src/pages/staff/StaffServiceRequests.tsx — API-Calls
 * ueber den staffRequest-Helper in services/api.ts (admin-api), useToast
 * auf org-portal-Konvention (../../components/ui → showToast(type, msg)).
 */

import { useCallback, useEffect, useState } from 'react'
import { api } from '../../services/api'
import type { StaffServiceRequest } from '../../services/api'
import { useToast } from '../../components/ui'

type Tab = 'pending' | 'in_progress' | 'history'

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'pending', label: 'Ausstehend', icon: '⏳' },
  { key: 'in_progress', label: 'In Bearbeitung', icon: '🔄' },
  { key: 'history', label: 'Verlauf', icon: '📋' },
]

const STATUS_BADGE: Record<string, { label: string; bg: string; fg: string }> = {
  pending: { label: 'Ausstehend', bg: '#fef3c7', fg: '#92400e' },
  in_progress: { label: 'In Bearbeitung', bg: '#dbeafe', fg: '#1e40af' },
  completed: { label: 'Abgeschlossen', bg: '#d1fae5', fg: '#065f46' },
  rejected: { label: 'Abgelehnt', bg: '#fee2e2', fg: '#991b1b' },
  cancelled: { label: 'Storniert', bg: '#f3f4f6', fg: '#374151' },
}

export default function StaffServiceRequests() {
  const [tab, setTab] = useState<Tab>('pending')
  const [requests, setRequests] = useState<StaffServiceRequest[]>([])
  const [loading, setLoading] = useState(true)
  const { showToast } = useToast()
  const toastError = useCallback((msg: string) => showToast('error', msg), [showToast])
  const toastSuccess = useCallback((msg: string) => showToast('success', msg), [showToast])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const fetcher =
        tab === 'pending'
          ? api.staffListPendingRequests
          : tab === 'in_progress'
            ? api.staffListInProgressRequests
            : api.staffListRequestHistory
      const resp = await fetcher.call(api)
      setRequests(resp.requests ?? [])
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as { error?: string }).error ?? err)
      toastError(`Fehler beim Laden: ${msg}`)
    } finally {
      setLoading(false)
    }
  }, [tab, toastError])

  useEffect(() => {
    load()
  }, [load])

  async function handleAction(action: 'complete' | 'reject', req: StaffServiceRequest) {
    const prompt = action === 'reject' ? window.prompt('Ablehnungsgrund?') : ''
    if (action === 'reject' && !prompt) return
    try {
      if (action === 'complete') {
        await api.staffCompleteRequest(req.id)
        toastSuccess('Anfrage abgeschlossen')
      } else {
        await api.staffRejectRequest(req.id, prompt!)
        toastSuccess('Anfrage abgelehnt')
      }
      load()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as { error?: string }).error ?? err)
      toastError(`Aktion fehlgeschlagen: ${msg}`)
    }
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>📨 Service Requests</h1>
        <p className="page-subtitle">Inbox eingehender Kunden-Anfragen</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid #e5e7eb' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === t.key ? '2px solid #2563eb' : '2px solid transparent',
              color: tab === t.key ? '#2563eb' : '#6b7280',
              fontWeight: tab === t.key ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            <span style={{ marginRight: 6 }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 13, alignSelf: 'center' }}>
          {loading ? 'Lade…' : `${requests.length} Eintr${requests.length === 1 ? 'ag' : 'äge'}`}
        </span>
      </div>

      {!loading && requests.length === 0 && (
        <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
          Keine Anfragen in diesem Tab.
        </div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {requests.map(req => {
          const badge = STATUS_BADGE[req.status] ?? { label: req.status, bg: '#f3f4f6', fg: '#374151' }
          return (
            <div
              key={req.id}
              style={{
                padding: 16,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                display: 'flex',
                gap: 16,
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <strong>{req.service_name}</strong>
                  <span
                    style={{
                      padding: '2px 8px',
                      background: badge.bg,
                      color: badge.fg,
                      borderRadius: 12,
                      fontSize: 12,
                    }}
                  >
                    {badge.label}
                  </span>
                </div>
                <div style={{ color: '#6b7280', fontSize: 14 }}>
                  Kunde: <code>{req.customer_email}</code>
                  {' · '}
                  Service: <code>{req.service_slug}</code>
                </div>
                <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>
                  Erstellt: {new Date(req.created_at).toLocaleString('de-DE')}
                  {req.assigned_to && <> · Zugewiesen an: {req.assigned_to}</>}
                </div>
                {req.admin_notes && (
                  <div style={{ marginTop: 8, padding: 8, background: '#f9fafb', borderRadius: 4, fontSize: 13 }}>
                    {req.admin_notes}
                  </div>
                )}
              </div>
              {tab !== 'history' && (
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button
                    onClick={() => handleAction('complete', req)}
                    style={{
                      padding: '6px 12px',
                      background: '#10b981',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    Abschließen
                  </button>
                  <button
                    onClick={() => handleAction('reject', req)}
                    style={{
                      padding: '6px 12px',
                      background: '#fff',
                      color: '#991b1b',
                      border: '1px solid #fecaca',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    Ablehnen
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
